import { Button, Tooltip, useComputedColorScheme } from '@mantine/core';
import { BaseRecord } from '@refinedev/core';
import { IconNotebook, IconPlus } from '@tabler/icons-react';
import { MRT_TableInstance } from 'mantine-react-table';
import { Dispatch, SetStateAction } from 'react';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { buttonTextColor } from 'utils/buttonUtils';

interface CreateRowButtonProps<TData extends BaseRecord> {
  type: string;
  table: MRT_TableInstance<TData>;
  isLoading?: boolean;
  disabled?: boolean;
  setCreateModalType?: Dispatch<SetStateAction<string>>;
  modalType?: 'claim' | 'bulk' | 'repeat';
  label?: string;
}

export const CreateRowButton = <TData extends BaseRecord>({
  type,
  table,
  isLoading = false,
  disabled = false,
  setCreateModalType,
  modalType = 'claim',
  label,
}: CreateRowButtonProps<TData>) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const { getCreateDraft, getBulkDraft } = useClaimDraftStore();
  const isClaimLike = modalType === 'claim' || modalType === 'repeat';

  const handleCreateRow = () => {
    if (setCreateModalType) {
      setCreateModalType(modalType);
    }
    table.setCreatingRow(true);
  };

  const hasClaimCreateDraft = isClaimLike && !!getCreateDraft();
  const hasBulkCreateDraft = type === 'Batch' && !!getBulkDraft();
  const hasDraft = hasClaimCreateDraft || hasBulkCreateDraft;
  const dataTestSuffix = type.toLowerCase().replace(/\s+/g, '-');
  const buttonLabel = label ?? `Create ${type}`;

  return (
    <Tooltip label="Loading..." position="top" withArrow disabled={!isLoading}>
      <Tooltip
        label={hasDraft ? 'You have an unsaved draft' : ''}
        position="top"
        withArrow
        disabled={!hasDraft}
      >
        <Button
          onClick={handleCreateRow}
          variant="outline"
          radius="md"
          disabled={isLoading || disabled}
          data-variant="create"
          data-testid={`create-${dataTestSuffix}-button`}
          c={buttonTextColor(computedColorScheme, isLoading || disabled)}
          leftSection={hasDraft ? <IconNotebook /> : <IconPlus />}
        >
          {buttonLabel}
        </Button>
      </Tooltip>
    </Tooltip>
  );
};
